import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Save, FolderOpen, Trash2, Loader2 } from "lucide-react";

/**
 * Props:
 *  - fields: current fields array
 *  - scale: current scale
 *  - pageCount: number of pages in current template
 *  - onLoad(template): applies template.fields (and scale, page_count) to editor
 *  - canSave: boolean (need at least one field + template loaded)
 */
export default function TemplatesBar({ fields, scale, pageCount, onLoad, canSave }) {
  const t = useT();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/templates");
      setTemplates(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const saveTemplate = async () => {
    if (!name.trim()) return toast.error(t("templates.toasts.nameRequired"));
    if (!canSave) return toast.error(t("templates.toasts.cannotSave"));
    setSaving(true);
    try {
      const cleaned = fields.map((f) => ({
        page: f.page, x: f.x, y: f.y, text: f.text, font_size: f.font_size,
      }));
      await api.post("/templates", { name: name.trim(), fields: cleaned, scale, page_count: pageCount });
      toast.success(t("templates.toasts.saved"));
      setName("");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeTemplate = async (id) => {
    if (!window.confirm(t("templates.confirmDelete"))) return;
    try {
      await api.delete(`/templates/${id}`);
      toast.success(t("templates.toasts.deleted"));
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="border border-white/[0.08] rounded-sm p-4 bg-[#0A1738]/40" data-testid="templates-bar">
      <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40 mb-3">
        {t("templates.title")}
      </div>
      <div className="flex gap-2 mb-3">
        <Input
          data-testid="template-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("templates.namePlaceholder")}
          className="rounded-sm h-9 text-sm"
        />
        <Button
          data-testid="save-template-btn"
          onClick={saveTemplate}
          disabled={saving || !canSave}
          size="sm"
          className="rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] h-9 px-3"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        </Button>
      </div>
      <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="templates-list">
        {loading ? (
          <div className="font-geist-mono text-[10px] text-white/40">{t("templates.loading")}</div>
        ) : templates.length === 0 ? (
          <div className="font-geist-mono text-[10px] text-white/40">{t("templates.empty")}</div>
        ) : (
          templates.map((tpl) => (
            <div key={tpl.id} className="flex items-center justify-between border border-white/[0.08] rounded-sm px-2 py-1.5 hover:border-white/40 transition-colors">
              <button
                data-testid={`load-template-${tpl.id}`}
                onClick={() => { onLoad(tpl); toast.success(t("templates.toasts.loaded", { name: tpl.name })); }}
                className="flex items-center gap-2 min-w-0 flex-1 text-left"
              >
                <FolderOpen className="h-3.5 w-3.5 text-[#4D8AFF] flex-shrink-0" />
                <span className="text-xs truncate">{tpl.name}</span>
                <span className="font-geist-mono text-[9px] text-white/40 ml-1">
                  {t("templates.fieldsCount", { n: tpl.fields.length })}
                </span>
              </button>
              <button
                data-testid={`delete-template-${tpl.id}`}
                onClick={() => removeTemplate(tpl.id)}
                className="text-[#E63946] hover:bg-[#E63946]/10 p-1 rounded-sm"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
